/*
 * Quick add from a product card.
 *
 * Single-variant products need nothing here: Dawn's <product-form>
 * already turns the "+" submit into an AJAX add.
 *
 * Multi-variant products open a size sheet that rises from the bottom
 * of the viewport. There is exactly one sheet for the whole page, built
 * on <body> the first time it is needed — it deliberately does NOT live
 * inside the card, because the cards animate with `transform` on hover
 * and a transformed ancestor becomes the containing block for
 * position: fixed, which would trap the sheet inside the card box.
 *
 * The sheet is presentation only. Picking a size writes the variant id
 * back into that card's own form and submits it, so the actual add stays
 * with <product-form> and the cart notification, exactly as it is for
 * single-variant products.
 *
 * Pairs with snippets/unrols-quick-add.liquid and unrols-quick-add.css.
 */

const UnrolsSizeSheet = {
  el: null,
  onPick: null,
  lastFocus: null,

  build() {
    if (this.el) return this.el;

    const sheet = document.createElement('div');
    sheet.className = 'unrols-sheet';
    sheet.hidden = true;
    sheet.innerHTML = `
      <div class="unrols-sheet__scrim" data-sheet-close></div>
      <div class="unrols-sheet__panel" role="dialog" aria-modal="true" aria-labelledby="UnrolsSheetTitle">
        <div class="unrols-sheet__head">
          <div class="unrols-sheet__meta">
            <p class="unrols-sheet__title" id="UnrolsSheetTitle"></p>
            <p class="unrols-sheet__price"></p>
          </div>
          <button type="button" class="unrols-sheet__close" data-sheet-close aria-label="Close">
            <svg viewBox="0 0 14 14" aria-hidden="true" focusable="false">
              <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.5"/>
            </svg>
          </button>
        </div>
        <div class="unrols-sheet__media"></div>
        <p class="unrols-sheet__label"></p>
        <div class="unrols-sheet__sizes"></div>
      </div>
    `;

    sheet.addEventListener('click', (event) => {
      if (event.target.closest('[data-sheet-close]')) {
        this.close();
        return;
      }
      const choice = event.target.closest('.unrols-sheet__size');
      if (choice && !choice.disabled) {
        const pick = this.onPick;
        const id = choice.dataset.variantId;
        this.close();
        if (pick) pick(id);
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !sheet.hidden) this.close();
    });

    document.body.appendChild(sheet);
    this.el = sheet;
    return sheet;
  },

  open(data, onPick) {
    const sheet = this.build();
    this.onPick = onPick;
    this.lastFocus = document.activeElement;

    sheet.querySelector('.unrols-sheet__title').textContent = data.title || '';
    sheet.querySelector('.unrols-sheet__price').textContent = data.price || '';
    sheet.querySelector('.unrols-sheet__label').textContent = `Select ${data.optionName || 'size'}`;

    const media = sheet.querySelector('.unrols-sheet__media');
    media.innerHTML = '';
    (data.images || []).forEach((src) => {
      const img = document.createElement('img');
      img.className = 'unrols-sheet__img';
      img.src = src;
      img.alt = '';
      img.loading = 'lazy';
      media.appendChild(img);
    });
    media.hidden = media.children.length === 0;

    const sizes = sheet.querySelector('.unrols-sheet__sizes');
    sizes.innerHTML = '';
    (data.variants || []).forEach((variant) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'unrols-sheet__size';
      button.dataset.variantId = variant.id;
      button.textContent = variant.title;
      button.disabled = !variant.available;
      sizes.appendChild(button);
    });

    sheet.hidden = false;
    // Two frames: one to get the element painted in its off-screen
    // position, one to flip the class so the transition actually runs.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => sheet.classList.add('is-open'));
    });

    document.body.classList.add('unrols-sheet-open');

    const firstSize = sizes.querySelector('.unrols-sheet__size:not([disabled])');
    if (firstSize) firstSize.focus();
  },

  close() {
    const sheet = this.el;
    if (!sheet || sheet.hidden) return;

    sheet.classList.remove('is-open');
    document.body.classList.remove('unrols-sheet-open');
    this.onPick = null;

    const hide = () => {
      sheet.hidden = true;
    };
    // Wait for the slide-down before hiding, but never hang on a
    // transitionend that will not fire (reduced motion, hidden tab).
    sheet.addEventListener('transitionend', hide, { once: true });
    setTimeout(hide, 400);

    if (this.lastFocus && document.contains(this.lastFocus)) this.lastFocus.focus();
    this.lastFocus = null;
  },
};

if (!customElements.get('unrols-quick-add')) {
  customElements.define(
    'unrols-quick-add',
    class UnrolsQuickAdd extends HTMLElement {
      connectedCallback() {
        this.dataScript = this.querySelector('.unrols-qa__data');

        // Single variant: <product-form> handles the whole interaction.
        if (!this.dataScript) return;

        this.form = this.querySelector('form');
        this.button = this.querySelector('.unrols-qa__btn');
        this.variantInput = this.querySelector('input[name="id"]');
        if (!this.form || !this.button || !this.variantInput) return;

        this.button.addEventListener('click', (event) => {
          // Stops the submit that would otherwise add the first variant.
          event.preventDefault();

          const data = this.productData();
          if (!data) return;

          UnrolsSizeSheet.open(data, (variantId) => {
            this.variantInput.value = variantId;
            // Not form.submit() — that skips the submit event, which is
            // the only thing ProductForm listens for, and would do a
            // full page POST. Passing the button as the submitter
            // matches a real click without dispatching one.
            this.form.requestSubmit(this.button);
          });
        });
      }

      productData() {
        if (this.parsed !== undefined) return this.parsed;
        try {
          this.parsed = JSON.parse(this.dataScript.textContent);
        } catch (error) {
          console.error('unrols-quick-add: could not parse product data', error);
          this.parsed = null;
        }
        return this.parsed;
      }
    }
  );
}
